// Inventory V2 — API client and TypeScript types

// ============================================================================
// Types
// ============================================================================

export interface Supplier {
  id: number;
  name: string;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  website: string | null;
  notes: string | null;
  is_active: boolean;
  item_count?: number;
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  item_count?: number;
  created_at: string;
  updated_at: string;
}

export interface InventoryItem {
  id: number;
  name: string;
  normalized_name: string;
  supplier_id: number | null;
  supplier_name?: string;
  brand: string | null;
  category_id: number | null;
  category_name?: string;
  base_unit: string;
  purchase_unit: string | null;
  purchase_quantity: number | null;
  purchase_cost: number | null;
  on_hand_quantity: number;
  threshold_quantity: number | null;
  par_level: number | null;
  last_received_unit_price: number | null;
  average_unit_price: number | null;
  weighted_average_unit_price: number | null;
  last_received_at: string | null;
  status: 'low' | 'moderate' | 'high' | 'out-of-stock';
  is_active: boolean;
  price_manually_overridden: boolean;
  notes: string | null;
  sku: string | null;
  supplier_sku: string | null;
  stock_known?: boolean;
  stock_version?: number;
  pack_size?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReceivingInvoice {
  id: number;
  supplier_id: number | null;
  supplier_name?: string;
  invoice_number: string | null;
  invoice_date: string | null;
  invoice_file_url: string | null;
  invoice_file_id?: string | null;
  stock_effect?: 'receive_now' | 'history_only';
  subtotal: number | null;
  tax: number | null;
  discount: number | null;
  total: number | null;
  status: 'draft' | 'posted' | 'cancelled';
  posted_at: string | null;
  notes: string | null;
  line_count?: number;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface ReceivingLine {
  id: number;
  receiving_invoice_id: number;
  inventory_item_id: number | null;
  raw_item_name: string | null;
  matched_item_name: string | null;
  item_name?: string;
  brand: string | null;
  category_id: number | null;
  category_name?: string;
  quantity_received: number;
  unit: string;
  purchase_cost: number;
  unit_price: number;
  tax: number;
  discount: number;
  match_status: 'matched' | 'unmatched' | 'new_item' | 'skipped';
  match_confidence: number | null;
  notes: string | null;
}

export interface StockLedgerEntry {
  id: number;
  inventory_item_id: number;
  item_name?: string;
  movement_type: string;
  quantity_delta: number;
  unit: string;
  unit_cost: number | null;
  total_cost: number | null;
  reference_type: string | null;
  reference_id: number | null;
  notes: string | null;
  created_by: number | null;
  created_by_name?: string;
  created_at: string;
}

export interface DashboardData {
  summary: {
    total_items: number;
    low_stock_items: number;
    out_of_stock_items: number;
    total_inventory_value: number;
    missing_threshold: number;
    missing_supplier: number;
    missing_category: number;
    price_increases: number;
    received_last_7_days: number;
  };
  low_stock_items: Array<{
    id: number;
    name: string;
    brand: string | null;
    base_unit: string;
    on_hand_quantity: number;
    threshold_quantity: number | null;
    par_level: number | null;
    status: string;
    last_received_at: string | null;
    supplier_name: string | null;
    category_name: string | null;
    suggested_reorder_qty: number;
  }>;
  price_movements: Array<{
    id: number;
    name: string;
    brand: string | null;
    base_unit: string;
    last_received_unit_price: number;
    average_unit_price: number;
    weighted_average_unit_price: number;
    price_change_pct: number;
  }>;
  consumption_by_category: Array<{
    category_name: string | null;
    total_consumed: number;
    items_affected: number;
  }>;
  data_quality: {
    missing_supplier: number;
    missing_category: number;
    missing_threshold: number;
    missing_unit: number;
    duplicate_names: number;
  };
}

export interface ParSuggestion {
  item_id: number;
  item_name: string;
  brand: string | null;
  base_unit: string;
  supplier_name: string | null;
  category_name: string | null;
  on_hand: number;
  current_par: number | null;
  suggested_par: number | null;
  difference: number | null;
  confidence: 'none' | 'low' | 'medium' | 'high';
  usage_7_days: number;
  usage_30_days: number;
  usage_90_days: number;
  avg_daily_usage: number | null;
  days_remaining: number | null;
  status: string;
}

export interface ImportPreviewRow {
  row_index: number;
  name: string | null;
  normalized_name: string | null;
  brand: string | null;
  supplier_name: string | null;
  unit: string | null;
  purchase_quantity: number | null;
  purchase_cost: number | null;
  unit_price: number | null;
  on_hand_quantity: number;
  errors: string[];
  exists_in_db: boolean;
  will_skip: boolean;
}

// ============================================================================
// API helpers
// ============================================================================

const headers = (token: string | null, extra?: Record<string, string>) => ({
  'X-Session-Token': token || '',
  ...(extra || {}),
});

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try { msg = JSON.parse(text).error || text; } catch { /* leave text */ }
    throw Object.assign(new Error(msg || `HTTP ${res.status}`), { status: res.status });
  }
  return res.json() as Promise<T>;
}

// ============================================================================
// Dashboard
// ============================================================================

export const getDashboard = (token: string | null) =>
  fetch('/api/v2/inventory-dashboard', { cache: 'no-store', headers: headers(token) })
    .then(handle<DashboardData>);

// ============================================================================
// Inventory Items
// ============================================================================

export interface ItemFilters {
  search?: string;
  supplier_id?: number;
  category_id?: number;
  status?: string;
  low_stock?: boolean;
  include_inactive?: boolean;
  limit?: number;
}

export const listItems = (token: string | null, filters: ItemFilters = {}) => {
  const qs = new URLSearchParams();
  if (filters.search) qs.set('search', filters.search);
  if (filters.supplier_id) qs.set('supplier_id', String(filters.supplier_id));
  if (filters.category_id) qs.set('category_id', String(filters.category_id));
  if (filters.status) qs.set('status', filters.status);
  if (filters.low_stock) qs.set('low_stock', 'true');
  if (filters.include_inactive) qs.set('include_inactive', 'true');
  if (filters.limit) qs.set('limit', String(filters.limit));
  const q = qs.toString() ? `?${qs}` : '';
  return fetch(`/api/v2/inventory${q}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<{ items: InventoryItem[] }>);
};

export const getItem = (token: string | null, id: number) =>
  fetch(`/api/v2/inventory/${id}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<{ item: InventoryItem; receiving_history: any[]; stock_ledger: StockLedgerEntry[] }>);

export const createItem = (token: string | null, body: Partial<InventoryItem> & { price_per_unit_override?: number }) =>
  fetch('/api/v2/inventory', {
    method: 'POST', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ item: InventoryItem }>);

export const updateItem = (token: string | null, id: number, body: Partial<InventoryItem>) =>
  fetch(`/api/v2/inventory/${id}`, {
    method: 'PATCH', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ item: InventoryItem }>);

export const deleteItem = (token: string | null, id: number) =>
  fetch(`/api/v2/inventory/${id}`, { method: 'DELETE', headers: headers(token) })
    .then(handle<{ item?: InventoryItem; message: string }>);

// ============================================================================
// Suppliers
// ============================================================================

export const listSuppliers = (token: string | null, includeInactive = false) =>
  fetch(`/api/v2/suppliers${includeInactive ? '?include_inactive=true' : ''}`, {
    cache: 'no-store', headers: headers(token),
  }).then(handle<{ suppliers: Supplier[] }>);

export const getSupplier = (token: string | null, id: number) =>
  fetch(`/api/v2/suppliers/${id}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<{ supplier: Supplier; items: InventoryItem[]; invoices: ReceivingInvoice[] }>);

export const createSupplier = (token: string | null, body: Partial<Supplier>) =>
  fetch('/api/v2/suppliers', {
    method: 'POST', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ supplier: Supplier }>);

export const updateSupplier = (token: string | null, id: number, body: Partial<Supplier>) =>
  fetch(`/api/v2/suppliers/${id}`, {
    method: 'PATCH', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ supplier: Supplier }>);

export const deleteSupplier = (token: string | null, id: number) =>
  fetch(`/api/v2/suppliers/${id}`, { method: 'DELETE', headers: headers(token) })
    .then(handle<{ supplier: Supplier }>);

// ============================================================================
// Categories
// ============================================================================

export const listCategories = (token: string | null) =>
  fetch('/api/v2/categories', { cache: 'no-store', headers: headers(token) })
    .then(handle<{ categories: Category[] }>);

export const createCategory = (token: string | null, body: Partial<Category>) =>
  fetch('/api/v2/categories', {
    method: 'POST', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ category: Category }>);

export const updateCategory = (token: string | null, id: number, body: Partial<Category>) =>
  fetch(`/api/v2/categories/${id}`, {
    method: 'PATCH', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ category: Category }>);

export const deleteCategory = (token: string | null, id: number) =>
  fetch(`/api/v2/categories/${id}`, { method: 'DELETE', headers: headers(token) })
    .then(handle<{ category: Category }>);

// ============================================================================
// Receiving Invoices
// ============================================================================

export const listReceivingInvoices = (token: string | null, filters: { supplier_id?: number; status?: string } = {}) => {
  const qs = new URLSearchParams();
  if (filters.supplier_id) qs.set('supplier_id', String(filters.supplier_id));
  if (filters.status) qs.set('status', filters.status);
  const q = qs.toString() ? `?${qs}` : '';
  return fetch(`/api/v2/receiving${q}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<{ invoices: ReceivingInvoice[] }>);
};

export const getReceivingInvoice = (token: string | null, id: number) =>
  fetch(`/api/v2/receiving/${id}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<{ invoice: ReceivingInvoice; lines: ReceivingLine[] }>);

export const createReceivingInvoice = (token: string | null, body: any) =>
  fetch('/api/v2/receiving', {
    method: 'POST', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ invoice: ReceivingInvoice }>);

export const updateReceivingInvoice = (token: string | null, id: number, body: any) =>
  fetch(`/api/v2/receiving/${id}`, {
    method: 'PATCH', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ invoice: ReceivingInvoice }>);

export const postReceivingInvoice = (token: string | null, id: number) =>
  fetch(`/api/v2/receiving/${id}/post`, {
    method: 'POST', headers: headers(token),
  }).then(handle<{ message: string; invoice_id: number }>);

export const cancelReceivingInvoice = (token: string | null, id: number) =>
  fetch(`/api/v2/receiving/${id}`, { method: 'DELETE', headers: headers(token) })
    .then(handle<{ invoice: ReceivingInvoice }>);

// ============================================================================
// Stock Ledger
// ============================================================================

export const listStockLedger = (token: string | null, itemId?: number, limit = 50, offset = 0) => {
  const path = itemId ? `/api/v2/stock-ledger/${itemId}` : '/api/v2/stock-ledger';
  return fetch(`${path}?limit=${limit}&offset=${offset}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<{ entries: StockLedgerEntry[]; total: number; limit: number; offset: number }>);
};

export const createStockAdjustment = (token: string | null, body: {
  inventory_item_id: number;
  movement_type: string;
  quantity_delta: number;
  unit: string;
  unit_cost?: number;
  notes?: string;
}) =>
  fetch('/api/v2/stock-ledger', {
    method: 'POST', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ entry: StockLedgerEntry }>);

// ============================================================================
// Import
// ============================================================================

export const listImportJobs = (token: string | null) =>
  fetch('/api/v2/inventory-import', { cache: 'no-store', headers: headers(token) })
    .then(handle<{ imports: any[] }>);

export const createImportJob = (token: string | null, body: { source_type: string; rows: any[]; file_url?: string }) =>
  fetch('/api/v2/inventory-import', {
    method: 'POST', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }).then(handle<{ import_job: any; preview: ImportPreviewRow[]; error_count: number; valid_count: number }>);

export const confirmImport = (token: string | null, id: number) =>
  fetch(`/api/v2/inventory-import/${id}/confirm`, {
    method: 'POST', headers: headers(token),
  }).then(handle<{ message: string; successful: number; failed: number; errors: any[] }>);

// ============================================================================
// Par Suggestions
// ============================================================================

export const getParSuggestions = (token: string | null, config?: { coverage_days?: number; lead_time_days?: number; safety_stock_pct?: number }) => {
  const qs = new URLSearchParams();
  if (config?.coverage_days) qs.set('coverage_days', String(config.coverage_days));
  if (config?.lead_time_days) qs.set('lead_time_days', String(config.lead_time_days));
  if (config?.safety_stock_pct) qs.set('safety_stock_pct', String(config.safety_stock_pct));
  const q = qs.toString() ? `?${qs}` : '';
  return fetch(`/api/v2/par-suggestions${q}`, { cache: 'no-store', headers: headers(token) })
    .then(handle<{ suggestions: ParSuggestion[]; config: any }>);
};

export const acceptParSuggestion = (token: string | null, itemId: number, parLevel: number) =>
  fetch('/api/v2/par-suggestions', {
    method: 'POST', headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ item_id: itemId, par_level: parLevel }),
  }).then(handle<{ item: InventoryItem }>);

// ============================================================================
// Invoice Scan
// ============================================================================

export interface InvoiceScanLineItem {
  row_index: number;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  unit_price: number | null;
  amount: number | null;
  product_code: string | null;
  matched_item: {
    id: number;
    name: string;
    base_unit: string;
    sku: string | null;
    supplier_sku: string | null;
  } | null;
}

export interface InvoiceScanResult {
  invoice_file_id?: string;
  supplier: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  currency: string;
  items: InvoiceScanLineItem[];
  matched_count: number;
  unmatched_count: number;
  message?: string;
}

export const scanInvoice = (token: string | null, fileBase64: string, contentType: string) =>
  fetch('/api/v2/invoice-scan', {
    method: 'POST',
    headers: headers(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ file_base64: fileBase64, content_type: contentType }),
  }).then(handle<InvoiceScanResult>);
